import React, { useState } from 'react';
import { Highlighter, X, Tag, ArrowUpRight, Trash2, Share2 } from 'lucide-react';
import ShareHighlightsModal from './ShareHighlightsModal';

/**
 * HighlightedInsights Component
 *
 * Displays all saved highlights from the College Notebook.
 * Organized by category with ability to:
 * - View highlighted text
 * - Jump to the source section
 * - Change category
 * - Delete highlights
 *
 * Features:
 * - Filter by category
 * - Jump navigation to source sections
 * - Edit/delete capabilities
 * - Visual category indicators
 */
export default function HighlightedInsights({
  highlights,
  loading,
  onDelete,
  onUpdateCategory,
  onJumpToSection,
  collegeName = ''
}) {
  const [filter, setFilter] = useState('all');
  const [expandedHighlight, setExpandedHighlight] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  // Categories configuration
  const categories = [
    { value: 'all', label: 'All', color: 'slate' },
    { value: 'positioning', label: 'Positioning', color: 'indigo' },
    { value: 'research', label: 'Research', color: 'azure' },
    { value: 'values', label: 'Values', color: 'coral' },
    { value: 'advantages', label: 'Advantages', color: 'teal' },
    { value: 'strategy', label: 'Strategy', color: 'gold' },
    { value: 'general', label: 'General', color: 'emerald' }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      positioning: 'border-indigo-500 bg-indigo-50',
      research: 'border-azure-500 bg-azure-50',
      values: 'border-coral-500 bg-coral-50',
      advantages: 'border-teal-500 bg-teal-50',
      strategy: 'border-gold-500 bg-gold-50',
      general: 'border-emerald-500 bg-emerald-50'
    };
    return colors[category] || colors.general;
  };

  const getBadgeColor = (category) => {
    const colors = {
      positioning: 'bg-indigo-500 text-white',
      research: 'bg-azure-500 text-white',
      values: 'bg-coral-500 text-white',
      advantages: 'bg-teal-500 text-white',
      strategy: 'bg-gold-500 text-white',
      general: 'bg-emerald-500 text-white'
    };
    return colors[category] || colors.general;
  };

  // Filter highlights
  const filteredHighlights = filter === 'all'
    ? highlights
    : highlights.filter(h => h.category === filter);

  // Group by category for display
  const groupedHighlights = filteredHighlights.reduce((acc, highlight) => {
    const cat = highlight.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(highlight);
    return acc;
  }, {});

  const handleDelete = async (highlightId) => {
    if (window.confirm('Delete this highlight?')) {
      try {
        await onDelete(highlightId);
      } catch (error) {
        console.error('Error deleting highlight:', error);
        alert('Failed to delete highlight');
      }
    }
  };

  const handleCategoryChange = async (highlightId, newCategory) => {
    try {
      await onUpdateCategory(highlightId, newCategory);
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (highlights.length === 0) {
    return (
      <div className="text-center py-8 px-4">
        <Highlighter className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-600 mb-2">No highlights yet</p>
        <p className="text-xs text-slate-400">
          Select text in any section to save highlights
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Share Button */}
      {highlights.length > 0 && (
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-medium text-slate-600">
            {highlights.length} highlight{highlights.length !== 1 ? 's' : ''} saved
          </h3>
          <button
            onClick={() => setShowShareModal(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Share2 className="w-3 h-3" />
            Share
          </button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-1 pb-3 border-b border-stone-200">
        {categories.map((cat) => {
          const count = cat.value === 'all'
            ? highlights.length
            : highlights.filter(h => h.category === cat.value).length;

          return (
            <button
              key={cat.value}
              onClick={() => setFilter(cat.value)}
              className={`px-3 py-1.5 text-xs rounded-full transition-all flex items-center gap-1 ${
                filter === cat.value
                  ? 'bg-slate-900 text-white font-medium'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat.label}
              {count > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                  filter === cat.value ? 'bg-white text-slate-900' : 'bg-slate-200'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Highlights List */}
      {filteredHighlights.length === 0 ? (
        <div className="text-center py-6 text-sm text-slate-500">
          No highlights in this category
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(groupedHighlights).map(([category, categoryHighlights]) => (
            <div key={category} className="space-y-2">
              {/* Category Header (only show if not filtering) */}
              {filter === 'all' && (
                <div className="flex items-center gap-2 text-xs font-bold text-slate-700 uppercase tracking-wide mt-4">
                  <Tag className="w-3 h-3" />
                  {categories.find(c => c.value === category)?.label || 'General'}
                </div>
              )}

              {/* Highlights in this category */}
              {categoryHighlights.map((highlight) => (
                <div
                  key={highlight.id}
                  className={`border-l-4 ${getCategoryColor(highlight.category)} rounded-lg p-3 transition-all hover:shadow-md`}
                >
                  {/* Highlight Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getBadgeColor(highlight.category)}`}>
                          {categories.find(c => c.value === highlight.category)?.label || 'General'}
                        </span>
                        {highlight.section_name && (
                          <button
                            onClick={() => onJumpToSection(highlight.section_name)}
                            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                            title="Jump to section"
                          >
                            <span className="truncate">{highlight.section_name}</span>
                            <ArrowUpRight className="w-3 h-3 flex-shrink-0" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <button
                      onClick={() => handleDelete(highlight.id)}
                      className="p-1 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                      title="Delete highlight"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </button>
                  </div>

                  {/* Highlighted Text */}
                  <p className="text-xs text-slate-700 leading-relaxed mb-2 italic">
                    "{highlight.highlighted_text}"
                  </p>

                  {/* Context (if available) */}
                  {highlight.context && (
                    <p className="text-xs text-slate-500 pl-3 border-l-2 border-slate-200">
                      Context: {highlight.context}
                    </p>
                  )}

                  {/* Category Selector (expanded) */}
                  {expandedHighlight === highlight.id && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-medium text-slate-600 mb-2">Change category:</p>
                      <div className="flex flex-wrap gap-1">
                        {categories.filter(c => c.value !== 'all').map((cat) => (
                          <button
                            key={cat.value}
                            onClick={() => {
                              handleCategoryChange(highlight.id, cat.value);
                              setExpandedHighlight(null);
                            }}
                            disabled={highlight.category === cat.value}
                            className={`px-2 py-1 text-xs rounded-full transition-all ${
                              highlight.category === cat.value
                                ? 'bg-slate-900 text-white cursor-not-allowed'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Toggle category editor */}
                  <button
                    onClick={() => setExpandedHighlight(expandedHighlight === highlight.id ? null : highlight.id)}
                    className="text-xs text-slate-500 hover:text-slate-700 mt-2 transition-colors"
                  >
                    {expandedHighlight === highlight.id ? 'Cancel' : 'Change category'}
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareHighlightsModal
          highlights={highlights}
          collegeName={collegeName}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}
